import { useEffect, useId, useState } from 'react';

import {
  Box,
  Button,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  IconButton,
  Input,
  InputChip,
  Label,
  Legend,
  Paragraph,
  Popover,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconLockClosedOutline, IconPlusCircleFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyAmenityV2WebApiHandler_create,
  useRentalManagerUserPropertyAmenityV2WebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { DEFAULT_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import {
  useIsOnReviewStep,
  useMultiplexAmenityCreate,
  useMultiplexAmenityDelete,
  useMultiplexBuildingSessionId,
  useMultiplexListingFlow,
} from '../../../hooks/queries/multiplex';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';

import type { ChangeEvent } from 'react';
import type { LegendPropsInterface } from '@zillow/constellation-10';
import type {
  UserPropertyMultiplexAmenityCreateResponseV2WebDto,
  UserPropertyMultiplexAmenityDeleteResponseV2WebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const LOCK_BUTTON_TOOLTIP_CONTENT = {
  title: 'Need to edit these amenities?',
  body: 'These amenities are coming from your feed and must be edited in that system.',
};

const ADD_BUTTON_TOOLTIP_CONTENT = {
  title: 'Adding additional amenities',
  body: 'Added amenities will only display on Zillow Rental Network and must be edited or removed here.',
};

type TagProps = {
  id: string;
  amenity: string;
  isEditable: boolean;
};

type Props = {
  listingAlias: string;
  previouslySavedAmenities: Array<TagProps>;
  isInputFormDisabled: boolean;
  isFeed: boolean;
  isPropertyAmenitiesUpdateRestricted: boolean;
  label: string;
  labelSize?: LegendPropsInterface['size'];
};

const buildMultiplexAmenityErrorMessage = (
  response: UserPropertyMultiplexAmenityCreateResponseV2WebDto | UserPropertyMultiplexAmenityDeleteResponseV2WebDto,
): string | undefined => {
  const hasUnitFailure = response.units?.some((unit) => unit.success === false) ?? false;

  if (response.success !== false && !hasUnitFailure) {
    return undefined;
  }

  if (response.errorMessage) {
    return response.errorMessage;
  }

  const unitErrorMessage = response.units?.find((unit) => unit.success === false)?.errorMessage;
  return unitErrorMessage ?? 'Unable to save amenity';
};

const AdditionalAmenities = ({
  isInputFormDisabled = false,
  label,
  labelSize = 'md',
  listingAlias,
  previouslySavedAmenities = [],
  isFeed,
  isPropertyAmenitiesUpdateRestricted,
}: Props): JSX.Element => {
  const legendTextId = useId();
  const { enqueueToast } = useToast();

  const isOnReviewStep = useIsOnReviewStep();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  const saveViaMultiplex = Boolean(isInMultiplexListingFlow && !isOnReviewStep);

  const [tags, setTags] = useState([...previouslySavedAmenities]);
  const [additionalAmenityItem, setAdditionalAmenityItem] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [duplicateTagError, setDuplicateTagError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTags([...previouslySavedAmenities]);
  }, [previouslySavedAmenities]);

  const { mutate: createPropertyAmenity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyAmenityV2WebApiHandler_create,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyAmenityV2WebApiHandler_create',
        details: 'useRentalManagerUserPropertyAmenityV2WebApiHandler_create',
      },
      invalidates: ['properties-amenities-create'],
    },
  });

  const { mutate: deletePropertyAmenity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyAmenityV2WebApiHandler_delete,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyAmenityV2WebApiHandler_delete',
        details: 'useRentalManagerUserPropertyAmenityV2WebApiHandler_delete',
      },
      invalidates: ['properties-amenities-delete'],
    },
  });

  const { mutate: createMultiplexAmenity } = useMultiplexAmenityCreate({
    meta: {
      context: {
        api: 'useMultiplexAmenityCreate',
        details: 'AdditionalAmenities',
        fieldName: 'propertyAmenities',
      },
    },
  });

  const { mutate: deleteMultiplexAmenity } = useMultiplexAmenityDelete({
    meta: {
      context: {
        api: 'useMultiplexAmenityDelete',
        details: 'AdditionalAmenities',
        fieldName: 'propertyAmenities',
      },
    },
  });

  const hasTags = tags.length > 0;

  const addNewTagOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (duplicateTagError) {
      setDuplicateTagError(false);
    }
    if (errorMessage) {
      setErrorMessage(undefined);
    }
    setAdditionalAmenityItem(e.target.value);
  };

  const createAmenityOnBlurHandler = () => {
    if (additionalAmenityItem === '') {
      setErrorMessage(undefined);
    }
  };

  const createAmenity = () => {
    setDuplicateTagError(false);

    if (!additionalAmenityItem) {
      setErrorMessage('Property amenity cannot be blank');
      return;
    }
    setErrorMessage(undefined);

    const isDuplicateOfSavedTag = tags.some((tag) => tag.amenity === additionalAmenityItem);

    if (isDuplicateOfSavedTag) {
      setDuplicateTagError(true);
      return;
    }

    if (saveViaMultiplex) {
      if (!multiplexBuildingSessionId) {
        /**
         * Defensive guard only: multiplex save runs when `isInMultiplexListingFlow` is true, which should
         * already imply a session id. `useMultiplexUpdate` also rejects empty ids before any request.
         */
        logError({
          error: new Error('Unable to save: missing multiplexBuildingSessionId'),
          context: { details: 'AdditionalAmenities.createAmenity' },
        });
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        return;
      }

      createMultiplexAmenity(
        {
          postData: {
            multiplexBuildingSessionId,
            text: additionalAmenityItem,
          },
        },
        {
          onSuccess: (result) => {
            const responseErrorMessage = buildMultiplexAmenityErrorMessage(result);
            if (responseErrorMessage) {
              setErrorMessage(responseErrorMessage);
              return;
            }

            const amenityId = result.units?.find((unit) => unit.success)?.amenityId ?? result.result;
            setErrorMessage(undefined);
            setTags([...tags, { id: amenityId, amenity: additionalAmenityItem, isEditable: true }]);
            setAdditionalAmenityItem('');
            enqueueToast(<Toast tone="success">Amenity added</Toast>);
          },
          onError: (error) => {
            setErrorMessage(DatarouterError.getDisplayMessage(error));
            logError({
              error,
              context: 'PropertyAmenities.createMultiplexAmenity',
            });
          },
        },
      );
      return;
    }

    createPropertyAmenity(
      {
        postData: {
          listingAlias,
          text: additionalAmenityItem,
        },
      },
      {
        onSuccess: (result) => {
          setErrorMessage(undefined);
          setTags([...tags, { id: result.amenityId, amenity: additionalAmenityItem, isEditable: true }]);
          setAdditionalAmenityItem('');
          enqueueToast(<Toast tone="success">Amenity added</Toast>);
        },
        onError: (error) => {
          setErrorMessage(DatarouterError.getDisplayMessage(error));
          logError({
            error,
            context: 'PropertyAmenities.createAmenity',
          });
        },
      },
    );
  };

  const deleteAmenity = (index: number) => {
    const tagToDelete = tags[index];

    setIsDeleting(true);

    if (saveViaMultiplex) {
      if (!multiplexBuildingSessionId) {
        /**
         * Defensive guard only: multiplex save runs when `isInMultiplexListingFlow` is true, which should
         * already imply a session id. `useMultiplexUpdate` also rejects empty ids before any request.
         */
        logError({
          error: new Error('Unable to save: missing multiplexBuildingSessionId'),
          context: { details: 'AdditionalAmenities.deleteAmenity' },
        });
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setIsDeleting(false);
        return;
      }

      deleteMultiplexAmenity(
        {
          postData: {
            multiplexBuildingSessionId,
            amenityId: tagToDelete.id,
          },
        },
        {
          onSuccess: (result) => {
            const responseErrorMessage = buildMultiplexAmenityErrorMessage(result);
            if (responseErrorMessage) {
              setErrorMessage(responseErrorMessage);
              return;
            }

            const tagsCopy = [...tags];
            tagsCopy.splice(index, 1);
            setTags(tagsCopy);
            enqueueToast(<Toast tone="success">Amenity deleted</Toast>);
          },
          onError: (error) => {
            const clientErrMsg =
              (error as DatarouterError)?.message ??
              'There was an error deleting your amenity, refresh the page and try again';
            setErrorMessage(clientErrMsg);
            logError({
              error,
              errorType: 'AdditionalAmenitiesError',
              context: 'PropertyAmenities.deleteMultiplexAmenity',
            });
          },
          onSettled: () => {
            setIsDeleting(false);
          },
        },
      );
      return;
    }

    deletePropertyAmenity(
      {
        postData: {
          amenityId: tagToDelete.id,
        },
      },
      {
        onSuccess: () => {
          const tagsCopy = [...tags];
          tagsCopy.splice(index, 1);
          setTags(tagsCopy);
          enqueueToast(<Toast tone="success">Amenity deleted</Toast>);
        },
        onError: (error) => {
          const clientErrMsg =
            (error as DatarouterError)?.message ??
            'There was an error deleting your amenity, refresh the page and try again';
          setErrorMessage(clientErrMsg);
          logError({
            error,
            errorType: 'AdditionalAmenitiesError',
            context: 'PropertyAmenities.deleteAmenity',
          });
        },
        onSettled: () => {
          setIsDeleting(false);
        },
      },
    );
  };

  return (
    <FieldSet
      data-testid="AdditionalAmenities"
      aria-labelledby={legendTextId}
      legend={
        <Legend size={labelSize} disabled={isInputFormDisabled} css={{ marginBottom: '400' }}>
          <Box css={{ display: 'inline-flex', flexDirection: 'row', gap: '200', alignItems: 'center' }}>
            <span id={legendTextId}>{label}</span>

            {isFeed && (
              <Popover
                header={
                  <Heading level={2} textStyle="body-lg-bold">
                    {LOCK_BUTTON_TOOLTIP_CONTENT.title}
                  </Heading>
                }
                body={<Paragraph>{LOCK_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                placement="top"
                trigger={
                  <IconButton
                    size="sm"
                    title="Edits are disabled"
                    tone="neutral"
                    emphasis="bare"
                    icon={<IconLockClosedOutline />}
                  />
                }
              />
            )}
          </Box>
        </Legend>
      }
    >
      {hasTags && (
        <ul>
          {tags.map((tag) =>
            !tag.isEditable ? (
              <li key={`feed-amenity-tag-${tag.id}`}>
                <Paragraph css={{ marginBottom: '200', color: 'text.subtle' }}>{tag.amenity}</Paragraph>
              </li>
            ) : null,
          )}
          <Box css={{ marginBottom: '200' }} />
        </ul>
      )}

      {hasTags && (
        <ul>
          {tags.map((tag, i) => (
            <li key={`amenity-tag-${tag.id}`}>
              <InputChip
                disabled={!tag.isEditable}
                isOpen={tag.isEditable}
                onClose={() => (isDeleting ? null : deleteAmenity(i))}
                data-testid={`AdditionalAmenities-Tag-${tag.amenity}`}
              >
                {tag.amenity}
              </InputChip>
              <Box css={{ marginBottom: '400' }} />
            </li>
          ))}
        </ul>
      )}

      {Boolean(!isInputFormDisabled || (!isPropertyAmenitiesUpdateRestricted && isFeed)) && (
        <FormField
          error={!!(duplicateTagError || errorMessage)}
          label={<Label>Describe amenity</Label>}
          control={
            <Box css={{ display: 'flex', gap: '400' }}>
              <Input
                size="sm"
                onChange={addNewTagOnChangeHandler}
                onBlur={createAmenityOnBlurHandler}
                value={additionalAmenityItem}
                id="additionalAmenitiesInput"
                fluid
                css={{
                  maxWidth: '340px',
                  md: {
                    width: '75%',
                  },
                }}
              />

              <Box css={{ display: 'flex', flexDirection: 'row', gap: '200' }}>
                <Button
                  size="sm"
                  tone="brand"
                  emphasis="filled"
                  onClick={createAmenity}
                  onBlur={createAmenityOnBlurHandler}
                  disabled={!additionalAmenityItem}
                  data-testid="AdditionalAmenities-AddButton"
                  aria-label={`Add ${additionalAmenityItem || 'amenity'} to property`}
                  icon={<IconPlusCircleFilled />}
                >
                  Add
                </Button>

                {isFeed && (
                  <Popover
                    header={
                      <Heading level={2} textStyle="body-lg-bold">
                        {ADD_BUTTON_TOOLTIP_CONTENT.title}
                      </Heading>
                    }
                    body={<Paragraph>{ADD_BUTTON_TOOLTIP_CONTENT.body}</Paragraph>}
                    placement="top"
                  />
                )}
              </Box>
            </Box>
          }
          description={
            (duplicateTagError && (
              <FormHelp data-testid="AdditionalAmenities-Error">You’ve already entered this amenity</FormHelp>
            )) ||
            (errorMessage && (
              <FormHelp data-testid="AdditionalAmenities-Error">
                <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
              </FormHelp>
            ))
          }
        />
      )}
    </FieldSet>
  );
};

export default AdditionalAmenities;
